import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAdminClients, getCaregivers, listPriorAuths, getAssessment, listTaReports, deleteClient } from '../utils/api';
import { paymentsApi, subscribe } from '../utils/paymentsStore';
import { CARE_FIELDS, withCareDetails, saveCareDetails, pickCareDetails } from '../utils/clientDetails';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, useToast, fmtDate, fmtMoney, fmtNum, Pager, usePager } from '../components/ui';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'authorizations', label: 'Authorizations' },
  { key: 'payments', label: 'Payments' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'ta', label: 'T&A reports' },
];

function authStatus(a) {
  if (a.status === 'pending_review') return 'pending_review';
  if (a.endDate && a.endDate < new Date().toISOString().slice(0, 10)) return 'expired';
  return 'active';
}

export default function ClientDetailPage() {
  const { clientId, tab = 'overview' } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [auths, setAuths] = useState(null);
  const [claims, setClaims] = useState(null);
  const [assessment, setAssessment] = useState(undefined);
  const [ta, setTa] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [careEdit, setCareEdit] = useState(null); // null, or the four fields while editing
  const [careSaving, setCareSaving] = useState(false);
  const [showToast, toastNode] = useToast();
  // One pager per tab; each goes back to page 1 when the client or tab changes.
  const authsPager = usePager(auths || [], [clientId, tab]);
  const claimsPager = usePager(claims || [], [clientId, tab]);
  const taPager = usePager(ta || [], [clientId, tab]);

  useEffect(() => {
    getAdminClients().then(r => { const found = (r.clients || []).find(c => c.clientId === clientId); setClient(found ? withCareDetails(found) : null); }).catch(() => setClient(null));
    getCaregivers().then(r => setUsers(r.users || [])).catch(() => {});
    listPriorAuths(clientId).then(r => setAuths(r.authorizations || [])).catch(() => setAuths([]));
    getAssessment(clientId).then(a => setAssessment(a || null)).catch(() => setAssessment(null));
    listTaReports(clientId).then(r => setTa(r.reports || r.items || (Array.isArray(r) ? r : []))).catch(() => setTa([]));
  }, [clientId]);

  useEffect(() => {
    const load = async () => {
      const all = await paymentsApi.listClaims();
      const name = (client?.clientName || '').toLowerCase();
      setClaims(all.filter(c => c.clientId === clientId || (name && c.patientName.toLowerCase() === name)));
    };
    if (client !== undefined) { load(); return subscribe(load); }
    return undefined;
  }, [clientId, client]);

  const caregivers = useMemo(() => (client?.assignedCaregivers || []).map(u => { const x = users.find(y => y.username === u); return x ? `${x.firstName || ''} ${x.lastName || ''}`.trim() : u; }), [client, users]);
  const activeAuth = useMemo(() => (auths || []).filter(a => authStatus(a) === 'active').sort((a, b) => (a.endDate || '').localeCompare(b.endDate || ''))[0], [auths]);
  const pay = useMemo(() => {
    const c = claims || [];
    const billed = c.reduce((s, x) => s + x.amountBilledCents, 0), paid = c.reduce((s, x) => s + x.amountPaidCents, 0);
    return { billed, paid, outstanding: billed - paid, open: c.filter(x => x.isOutstanding).length };
  }, [claims]);
  const manage = (open) => `/clients/manage?open=${open}&client=${encodeURIComponent(clientId)}`;
  const saveCare = async () => {
    setCareSaving(true);
    try {
      const next = await saveCareDetails(client, careEdit);
      setClient(next);
      setCareEdit(null);
      showToast('Care details saved');
    } catch (e) { showToast(e.message || 'Could not save care details', 'error'); }
    finally { setCareSaving(false); }
  };
  const onDelete = async () => {
    try { await deleteClient(clientId); navigate('/clients'); }
    catch (e) { showToast(e.message || 'Could not delete client', 'error'); setConfirmDelete(false); }
  };

  if (client === undefined) return <main className="main-content ops-main"><div className="ops-page"><Skeleton rows={6} /></div></main>;
  if (client === null) return <main className="main-content ops-main"><div className="ops-page"><Empty title="Client not found"><Link to="/clients" className="ops-link">Back to clients</Link></Empty></div></main>;

  const initials = (client.clientName || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <nav className="ops-cell-sub" style={{ marginBottom: 8 }}><Link to="/clients" className="ops-link">Clients</Link> / {client.clientName}</nav>
        <div className="ops-panel ops-client-head">
          <div className="ops-avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0 }}>{client.clientName}</h1>
            <div className="ops-meta-row">
              {client.dob && <span>DOB {fmtDate(client.dob)}</span>}
              {client.medicaidId && <span>Medicaid <span className="mono">{client.medicaidId}</span></span>}
              {client.memberId && <span>Member ID <span className="mono">{client.memberId}</span></span>}
              {activeAuth && <span>Payer {activeAuth.payer}</span>}
              <StatusPill status={client.serviceStatus === 'service-ended' ? 'service-ended' : 'active'} />
            </div>
            <div className="ops-cell-sub">Caregivers: {caregivers.length ? caregivers.join(', ') : 'Unassigned'}</div>
          </div>
          <div className="ops-actions ops-actions--row">
            <Link to={manage('edit')} className="ops-btn"><Icon name="edit" /> Edit client</Link>
            <Link to={manage('assessment')} className="ops-btn ops-btn--navy-outline">Assessment</Link>
            <Link to={manage('ta')} className="ops-btn ops-btn--teal-outline">T&amp;A Reports</Link>
            <Link to={manage('prior-auth')} className="ops-btn ops-btn--gold-outline">Prior Auths</Link>
            <Link to={`/payments?new=1&client=${encodeURIComponent(clientId)}`} className="ops-btn ops-btn--primary"><Icon name="plus" /> New claim</Link>
            {confirmDelete ? (
              <span className="ops-actions"><button type="button" className="ops-btn ops-btn--danger" onClick={onDelete}>Yes, delete client</button><button type="button" className="ops-btn" onClick={() => setConfirmDelete(false)}>Cancel</button></span>
            ) : (
              <button type="button" className="ops-btn ops-btn--danger" onClick={() => setConfirmDelete(true)}><Icon name="trash" /> Delete</button>
            )}
          </div>
        </div>

        <div className="ops-tabs" role="tablist" style={{ marginBottom: 16 }}>
          {TABS.map(t => <button key={t.key} type="button" role="tab" className={tab === t.key ? 'is-active' : ''} onClick={() => navigate(`/clients/${encodeURIComponent(clientId)}/${t.key}`)}>{t.label}</button>)}
        </div>

        {tab === 'overview' && (
          <>
            <div className="ops-kpis ops-kpis--4">
              <div className="ops-kpi"><span className="ops-kpi__label">Active authorization</span><span className="ops-kpi__value" style={{ fontSize: 20 }}>{activeAuth ? `Ends ${fmtDate(activeAuth.endDate)}` : 'None'}</span><span className="ops-kpi__sub">{activeAuth ? `${activeAuth.payer} · ${fmtNum(activeAuth.unitsAuthorized)} units` : 'No active authorization on file'}</span></div>
              <div className="ops-kpi"><span className="ops-kpi__label">Authorizations</span><span className="ops-kpi__value">{auths ? fmtNum(auths.length) : '…'}</span><span className="ops-kpi__sub">{auths ? `${auths.filter(a => authStatus(a) === 'pending_review').length} pending review` : ''}</span></div>
              <div className="ops-kpi"><span className="ops-kpi__label">Billed</span><span className="ops-kpi__value">{fmtMoney(pay.billed)}</span><span className="ops-kpi__sub">{claims ? `${claims.length} claims` : ''}</span></div>
              <div className={`ops-kpi${pay.outstanding > 0 ? ' ops-kpi--amber' : ''}`}><span className="ops-kpi__label">Outstanding</span><span className="ops-kpi__value">{fmtMoney(pay.outstanding)}</span><span className="ops-kpi__sub">{pay.open} open claims</span></div>
            </div>
            <Panel title="Care details" className="ops-care-panel" action={careEdit ? (
              <span className="ops-actions">
                <button type="button" className="ops-btn ops-btn--sm" disabled={careSaving} onClick={() => setCareEdit(null)}>Cancel</button>
                <button type="button" className="ops-btn ops-btn--primary ops-btn--sm" disabled={careSaving} onClick={saveCare}>{careSaving ? 'Saving' : 'Save'}</button>
              </span>
            ) : (
              <button type="button" className="ops-btn ops-btn--sm" onClick={() => setCareEdit(pickCareDetails(client))}><Icon name="edit" /> Edit details</button>
            )}>
              {careEdit ? (
                <form className="ops-dl ops-dl--4" onSubmit={e => { e.preventDefault(); saveCare(); }}>
                  {CARE_FIELDS.map(f => (
                    <div className="ops-field" key={f.key}>
                      <label htmlFor={`care-${f.key}`}>{f.label}</label>
                      <input id={`care-${f.key}`} className="ops-input" type={f.key === 'caseManagerPhone' ? 'tel' : 'text'} value={careEdit[f.key]} placeholder={f.placeholder} autoComplete="off"
                        onChange={e => setCareEdit(c => ({ ...c, [f.key]: e.target.value }))} />
                    </div>
                  ))}
                  <button type="submit" hidden>Save</button>
                </form>
              ) : (
                <dl className="ops-dl ops-dl--4">
                  {CARE_FIELDS.map(f => (
                    <div key={f.key}>
                      <dt>{f.label}</dt>
                      <dd className={f.key === 'memberId' || f.key === 'diagnosisCode' ? 'mono' : ''}>
                        {client[f.key] ? (f.key === 'caseManagerPhone' ? <a className="ops-link" href={`tel:${client[f.key].replace(/[^+\d]/g, '')}`}>{client[f.key]}</a> : client[f.key]) : <span className="muted">Not on file</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              <p className="ops-cell-sub" style={{ margin: '12px 0 0' }}>Filled in from the prior authorization document when it lists them. Anything missing can be typed in here.</p>
            </Panel>
            <div className="ops-grid-2">
              <Panel title="Authorizations" flush action={<button type="button" className="ops-link" onClick={() => navigate(`/clients/${encodeURIComponent(clientId)}/authorizations`)}>All authorizations</button>}>
                {!auths ? <Skeleton /> : auths.length === 0 ? <Empty>No authorizations on file for this client.</Empty> : (
                  <ul className="ops-list">{[...auths].sort((x, y) => (y.endDate || '').localeCompare(x.endDate || '')).slice(0, 5).map(a => <li key={a.authId}><div className="ops-list__main"><span className="ops-cell-main">{a.payer}{a.authNumber ? <span className="mono"> {a.authNumber}</span> : ''}</span><div className="ops-cell-sub">{fmtDate(a.startDate)} to {fmtDate(a.endDate)}{a.unitsAuthorized ? ` · ${fmtNum(a.unitsAuthorized)} units` : ''}</div></div><StatusPill status={authStatus(a)} /></li>)}</ul>
                )}
              </Panel>
              <Panel title="Recent claims" flush action={<button type="button" className="ops-link" onClick={() => navigate(`/clients/${encodeURIComponent(clientId)}/payments`)}>All claims</button>}>
                {!claims ? <Skeleton /> : claims.length === 0 ? <Empty>No claims recorded for this client.</Empty> : (
                  <ul className="ops-list">{claims.slice(0, 5).map(c => <li key={c.claimId}><div className="ops-list__main"><span className="ops-cell-main">{fmtDate(c.dosStart)} to {fmtDate(c.dosEnd)}</span><div className="ops-cell-sub">{c.payerName} · {c.claimNumber || 'no claim #'}</div></div><div style={{ textAlign: 'right' }}><div className="ops-cell-main">{fmtMoney(c.amountBilledCents)}</div><StatusPill status={c.status} /></div></li>)}</ul>
                )}
              </Panel>
            </div>
          </>
        )}

        {tab === 'authorizations' && (
          <Panel title="Prior authorizations" count={auths ? auths.length : ''} flush foot={auths && auths.length > 0 ? <Pager {...authsPager.pagerProps} label="authorizations" /> : null} action={<Link to={manage('prior-auth')} className="ops-btn ops-btn--primary ops-btn--sm"><Icon name="upload" /> Upload or review</Link>}>
            {!auths ? <Skeleton /> : auths.length === 0 ? <Empty title="No authorizations">Upload the first authorization from the legacy manage view.</Empty> : (
              <div className="ops-table-wrap"><table className="ops-table">
                <thead><tr><th>Auth #</th><th>Payer</th><th>Service</th><th>Start</th><th>End</th><th className="num">Units</th><th className="num">Units/Week</th><th className="num">Hours/Week</th><th>Status</th></tr></thead>
                <tbody>{authsPager.pageRows.map(a => <tr key={a.authId}><td className="mono">{a.authNumber || ''}</td><td>{a.payer}</td><td>{a.serviceType || ''}</td><td>{fmtDate(a.startDate)}</td><td>{fmtDate(a.endDate)}</td><td className="num">{a.unitsAuthorized ? `${fmtNum(a.unitsAuthorized)}${a.unitsPeriod ? ` / ${a.unitsPeriod}` : ''}` : ''}</td><td className="num">{a.unitsPerWeek ? fmtNum(a.unitsPerWeek) : <span className="muted">Not set</span>}</td><td className="num">{a.hoursPerWeek ? fmtNum(a.hoursPerWeek, 2) : <span className="muted">Not set</span>}</td><td><StatusPill status={authStatus(a)} /></td></tr>)}</tbody>
              </table></div>
            )}
          </Panel>
        )}

        {tab === 'payments' && (
          <Panel title="Claims" count={claims ? claims.length : ''} flush action={<Link to={`/payments?new=1&client=${encodeURIComponent(clientId)}`} className="ops-btn ops-btn--primary ops-btn--sm"><Icon name="plus" /> New claim</Link>}
            foot={claims && claims.length > 0 && <><Pager {...claimsPager.pagerProps} label="claims" /><div className="ops-stat-row"><div className="ops-stat"><b>{fmtMoney(pay.billed)}</b><span>Billed</span></div><div className="ops-stat"><b>{fmtMoney(pay.paid)}</b><span>Paid</span></div><div className="ops-stat"><b className={pay.outstanding > 0 ? 'neg' : ''}>{fmtMoney(pay.outstanding)}</b><span>Outstanding</span></div></div></>}>
            {!claims ? <Skeleton /> : claims.length === 0 ? <Empty>No claims recorded for this client.</Empty> : (
              <div className="ops-table-wrap"><table className="ops-table">
                <thead><tr><th>Service dates</th><th>Paid</th><th>Payer</th><th>Claim #</th><th className="num">Units</th><th className="num">Billed</th><th className="num">Paid</th><th className="num">Adj.</th><th>Status</th></tr></thead>
                <tbody>{claimsPager.pageRows.map(c => <tr key={c.claimId} className="is-clickable" onClick={() => navigate(`/payments?claim=${encodeURIComponent(c.claimId)}`)}><td>{fmtDate(c.dosStart)} to {fmtDate(c.dosEnd)}</td><td>{fmtDate(c.datePaid) || <span className="muted">Unpaid</span>}</td><td>{c.payerName}</td><td className="mono">{c.claimNumber}</td><td className="num">{fmtNum(c.unitsBilled)} / {fmtNum(c.unitsPaid)}</td><td className="num">{fmtMoney(c.amountBilledCents)}</td><td className="num">{fmtMoney(c.amountPaidCents)}</td><td className={`num${c.adjustmentCents > 0 ? ' neg' : ''}`}>{fmtMoney(c.adjustmentCents)}</td><td><StatusPill status={c.status} /></td></tr>)}</tbody>
              </table></div>
            )}
          </Panel>
        )}

        {tab === 'assessment' && (
          <Panel title="Assessment" action={<Link to={manage('assessment')} className="ops-btn ops-btn--primary ops-btn--sm"><Icon name="edit" /> Edit assessment</Link>}>
            {assessment === undefined ? <Skeleton /> : !assessment || Object.keys(assessment).length === 0 ? <Empty title="No assessment on file">Add one from the legacy manage view.</Empty> : (
              <div className="ops-review-grid">
                {Object.entries(assessment).filter(([k, v]) => !['clientId', 'updatedAt', 'createdAt'].includes(k) && v && typeof v !== 'object').slice(0, 24).map(([k, v]) => (
                  <div key={k}><div className="ops-label">{k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</div><div>{String(v)}</div></div>
                ))}
                {Object.entries(assessment).filter(([, v]) => Array.isArray(v)).map(([k, v]) => (
                  <div key={k} className="span-2"><div className="ops-label">{k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</div><div>{v.map(x => typeof x === 'object' ? JSON.stringify(x) : String(x)).join(', ')}</div></div>
                ))}
              </div>
            )}
          </Panel>
        )}

        {tab === 'ta' && (
          <Panel title="Time and attendance reports" count={ta ? ta.length : ''} flush foot={ta && ta.length > 0 ? <Pager {...taPager.pagerProps} label="reports" /> : null} action={<Link to={manage('ta')} className="ops-btn ops-btn--primary ops-btn--sm"><Icon name="upload" /> Upload report</Link>}>
            {!ta ? <Skeleton /> : ta.length === 0 ? <Empty>No T&A reports uploaded.</Empty> : (
              <ul className="ops-list">{taPager.pageRows.map((r, i) => <li key={r.reportId || i}><div className="ops-list__main"><span className="ops-cell-main">{r.fileName || r.name || r.reportId}</span><div className="ops-cell-sub">{r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString() : ''}{r.periodStart ? ` · ${fmtDate(r.periodStart)} to ${fmtDate(r.periodEnd)}` : ''}</div></div></li>)}</ul>
            )}
          </Panel>
        )}
      </div>
      {toastNode}
    </main>
  );
}
